import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, ExternalLink, Save, Upload, ImagePlus, Loader2, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../components/admin/RichTextEditor";

const HERO_VARIANTS = [
  { value: 0, label: "Use uploaded hero image only" },
  { value: 1, label: "Variant 1 — Courthouse" },
  { value: 2, label: "Variant 2 — Calculator" },
  { value: 3, label: "Variant 3 — Handshake" },
  { value: 4, label: "Variant 4 — Document signing" },
  { value: 5, label: "Variant 5 — Office desk" },
  { value: 6, label: "Variant 6 — City skyline" },
  { value: 7, label: "Variant 7 — Family/people" },
  { value: 8, label: "Variant 8 — Abstract finance" },
];

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 200);

const blank = {
  slug: "",
  title: "",
  h1: "",
  meta_description: "",
  category: "Guide",
  body_html: "",
  hero_image: "",
  hero_image_alt: "",
  hero_variant: 0,
  status: "draft",
  published_at: "",
};

export default function AdminBlogEditor() {
  const { slug: routeSlug } = useParams();
  const isNew = !routeSlug || routeSlug === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [originalSlug, setOriginalSlug] = useState("");
  const heroFileRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/operations/blog/posts/${encodeURIComponent(routeSlug)}`);
        setForm({
          slug: data.slug || "",
          title: data.title || "",
          h1: data.h1 || "",
          meta_description: data.meta_description || "",
          category: data.category || "Guide",
          body_html: data.body_html || "",
          hero_image: data.hero_image || "",
          hero_image_alt: data.hero_image_alt || "",
          hero_variant: data.hero_variant || 0,
          status: data.status || "published",
          published_at: data.published_at || "",
        });
        setOriginalSlug(data.slug);
        setSlugTouched(true);
      } catch (err) {
        toast.error(formatApiError(err));
        navigate("/admin/blog");
      } finally {
        setLoading(false);
      }
    })();
  }, [isNew, routeSlug, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitleChange = (v) => {
    setForm((f) => ({
      ...f,
      title: v,
      slug: !slugTouched ? slugify(v) : f.slug,
    }));
  };

  const onHeroPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (form.slug) fd.append("slug", form.slug);
      fd.append("folder", "blog");
      const { data } = await api.post("/operations/blog/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      update("hero_image", data.url);
      toast.success("Hero image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingHero(false);
    }
  };

  const validate = () => {
    if (!form.title.trim()) return "Title is required";
    if (!form.slug.trim()) return "Slug is required";
    if (form.title.length < 3) return "Title is too short";
    if (form.slug.length < 3) return "Slug is too short";
    return null;
  };

  const save = async (publishOverride) => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: slugify(form.slug),
        hero_variant: form.hero_variant ? Number(form.hero_variant) : null,
        status: publishOverride || form.status,
      };
      if (!payload.published_at) delete payload.published_at;

      if (isNew) {
        await api.post("/operations/blog/posts", payload);
        toast.success(payload.status === "published" ? "Published" : "Saved as draft");
      } else {
        await api.patch(`/operations/blog/posts/${encodeURIComponent(originalSlug)}`, payload);
        toast.success("Changes saved");
      }
      navigate(`/admin/blog/${encodeURIComponent(payload.slug)}`);
      // Reset state to reflect saved snapshot.
      setOriginalSlug(payload.slug);
      setForm((f) => ({ ...f, ...payload }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (isNew) return;
    if (!window.confirm(`Permanently delete "${form.title}"?\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/operations/blog/posts/${encodeURIComponent(originalSlug)}`);
      toast.success("Post deleted");
      navigate("/admin/blog");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicate = async () => {
    if (isNew) return;
    try {
      const { data } = await api.post(`/operations/blog/posts/${encodeURIComponent(originalSlug)}/duplicate`);
      toast.success("Duplicate created as draft");
      navigate(`/admin/blog/${encodeURIComponent(data.slug)}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) {
    return (
      <div className="p-10 text-center text-[#6B7280]" data-testid="blog-editor-loading">
        Loading post…
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="admin-blog-editor">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <Link to="/admin/blog" className="inline-flex items-center text-xs text-[#6B7280] hover:text-[#0066FF] mb-1.5">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back to all posts
          </Link>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]" data-testid="editor-title-heading">
            {isNew ? "New post" : "Edit post"}
          </h1>
          {!isNew && (
            <a
              href={`/blog/${originalSlug}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#0066FF] hover:underline inline-flex items-center gap-1 mt-1"
              data-testid="editor-view-public"
            >
              View public page <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <Button
              variant="outline"
              className="h-9"
              onClick={duplicate}
              data-testid="editor-duplicate-btn"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Duplicate
            </Button>
          )}
          {!isNew && (
            <Button
              variant="outline"
              className="h-9 text-[#B91C1C] hover:bg-[#FEF2F2]"
              onClick={remove}
              data-testid="editor-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <Button
            variant="outline"
            className="h-9"
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="editor-save-draft-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" /> Save draft
          </Button>
          <Button
            className="h-9 bg-[#0066FF] hover:bg-[#0052CC]"
            onClick={() => save("published")}
            disabled={saving}
            data-testid="editor-publish-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            {form.status === "published" && !isNew ? "Save & republish" : "Publish"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder="A clear, search-friendly headline"
                className="mt-1 text-lg font-medium"
                data-testid="editor-input-title"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Slug (URL)</Label>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-xs text-[#9CA3AF] whitespace-nowrap">/blog/</span>
                <Input
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); update("slug", slugify(e.target.value)); }}
                  placeholder="post-url-slug"
                  className="font-mono text-sm"
                  data-testid="editor-input-slug"
                />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-[#E5E7EB] flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Article body</Label>
              <span className="text-[11px] text-[#9CA3AF]">
                Insert <code className="px-1 py-0.5 bg-[#F3F4F6] rounded">{`{{CALCULATOR_WIDGET}}`}</code> to embed the calculator
              </span>
            </div>
            <RichTextEditor
              value={form.body_html}
              onChange={(html) => update("body_html", html)}
              placeholder="Write your article…"
              slug={form.slug}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Status</Label>
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger className="h-9" data-testid="editor-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft (hidden from public)</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            <Label className="text-xs uppercase tracking-wider text-[#6B7280] pt-2 block">Category</Label>
            <Input
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              placeholder="Guide, News, FAQ…"
              data-testid="editor-input-category"
            />
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">SEO</Label>
            <div>
              <Label className="text-[11px] text-[#6B7280]">H1 (defaults to title)</Label>
              <Input
                value={form.h1}
                onChange={(e) => update("h1", e.target.value)}
                placeholder="Optional alternate H1"
                className="mt-1"
                data-testid="editor-input-h1"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#6B7280]">Meta description</Label>
              <Textarea
                value={form.meta_description}
                onChange={(e) => update("meta_description", e.target.value)}
                placeholder="150–160 characters that summarize the article for search results."
                rows={3}
                className="mt-1 text-sm"
                data-testid="editor-input-meta"
              />
              <div className="text-[10px] text-[#9CA3AF] mt-1 tabular-nums">
                {form.meta_description.length} / 160
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Hero image</Label>
            {form.hero_image ? (
              <div className="relative rounded border border-[#E5E7EB] overflow-hidden">
                <img src={form.hero_image} alt={form.hero_image_alt || form.title} className="w-full h-32 object-cover" data-testid="editor-hero-preview" />
                <button
                  type="button"
                  onClick={() => update("hero_image", "")}
                  className="absolute top-1.5 right-1.5 h-6 w-6 rounded bg-black/70 text-white inline-flex items-center justify-center text-xs hover:bg-black/85"
                  data-testid="editor-hero-clear"
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => heroFileRef.current?.click()}
                disabled={uploadingHero}
                data-testid="editor-hero-upload-btn"
                className="w-full h-32 border-2 border-dashed border-[#E5E7EB] rounded flex flex-col items-center justify-center text-[#6B7280] hover:border-[#0066FF] hover:text-[#0066FF] transition-colors"
              >
                {uploadingHero ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ImagePlus className="w-5 h-5 mb-1" />
                    <span className="text-xs">Upload hero image</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={heroFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onHeroPicked}
              data-testid="editor-hero-input"
            />
            <Input
              value={form.hero_image}
              onChange={(e) => update("hero_image", e.target.value)}
              placeholder="…or paste an image URL"
              className="text-xs font-mono"
              data-testid="editor-input-hero-url"
            />
            <Input
              value={form.hero_image_alt}
              onChange={(e) => update("hero_image_alt", e.target.value)}
              placeholder="Alt text (for accessibility + SEO)"
              className="text-sm"
              data-testid="editor-input-hero-alt"
            />
            <Label className="text-[11px] text-[#6B7280] pt-2 block">Hero style variant</Label>
            <Select
              value={String(form.hero_variant || 0)}
              onValueChange={(v) => update("hero_variant", Number(v))}
            >
              <SelectTrigger className="h-9" data-testid="editor-hero-variant-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HERO_VARIANTS.map((v) => (
                  <SelectItem key={v.value} value={String(v.value)}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isNew && form.published_at && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">Published</div>
              <div className="text-sm text-[#0A2540] tabular-nums">
                {new Date(form.published_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
